import axios from 'axios';

const BASE_URL = 'https://api.homechamp.in/api/v1';

const api = axios.create({ baseURL: BASE_URL });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('admin_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Auto-logout on 401 expired token
api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_user');
      window.location.reload();
    }
    return Promise.reject(err);
  }
);

export const login = async (email, password) => {
  const form = new URLSearchParams();
  form.append('username', email);
  form.append('password', password);
  const res = await api.post('/auth/login', form, {
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
  });
  return res.data;
};

export const getStats = () => api.get('/admin/stats').then(r => r.data);
export const getUsers = () => api.get('/admin/users').then(r => r.data);
export const createUser = (data) => api.post('/admin/users', data).then(r => r.data);
export const deleteUser = (id) => api.delete(`/admin/users/${id}`).then(r => r.data);
export const toggleUser = (id) => api.put(`/admin/users/${id}/toggle`).then(r => r.data);
export const getWorkers = () => api.get('/admin/workers').then(r => r.data);
export const getWorkersEarnings = () => api.get('/admin/workers/earnings-report').then(r => r.data);
export const getWorkerRejections = (workerId) => api.get('/admin/workers/rejections' + (workerId ? `?worker_id=${workerId}` : '')).then(r => r.data);
export const verifyKyc = (id) => api.put(`/admin/workers/${id}/verify-kyc`).then(r => r.data);
export const verifyBank = (id) => api.put(`/admin/workers/${id}/verify-bank`).then(r => r.data);
export const removeWorker = (id) => api.delete(`/admin/workers/${id}`).then(r => r.data);
export const getBookings = () => api.get('/admin/bookings').then(r => r.data);
export const refundBooking = (id) => api.post(`/admin/bookings/${id}/refund`).then(r => r.data);
export const deleteBooking = (id) => api.delete(`/admin/bookings/${id}`).then(r => r.data);
export const updateCommission = (percent) => api.put(`/admin/commission?percent=${percent}`).then(r => r.data);
export const getPayments = () => api.get('/admin/payments').then(r => r.data);
export const getTickets = () => api.get('/support/admin/tickets').then(r => r.data);
export const updateTicket = (id, data) => api.put(`/support/admin/tickets/${id}`, data).then(r => r.data);
export const getTicketMessages = (id) => api.get(`/support/admin/tickets/${id}/messages`).then(r => r.data);
export const sendTicketMessage = (id, data) => api.post(`/support/admin/tickets/${id}/messages`, data).then(r => r.data);
export const getReviews = () => api.get('/admin/reviews').then(r => r.data);

// Categories
export const getCategories = () => api.get('/categories/').then(r => r.data);
export const createCategory = (data) => api.post('/categories/', data).then(r => r.data);
export const updateCategory = (id, data) => api.put(`/categories/${id}`, data).then(r => r.data);
export const deleteCategory = (id) => api.delete(`/categories/${id}`).then(r => r.data);
export const uploadCategoryImage = (id, file) => {
  const form = new FormData();
  form.append('file', file);
  return api.post(`/categories/${id}/upload-image`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }).then(r => r.data);
};

// Services (admin)
export const getServices = () => api.get('/admin/services').then(r => r.data);
export const createService = (data) => api.post('/admin/services', data).then(r => r.data);
export const updateService = (id, data) => api.put(`/admin/services/${id}`, data).then(r => r.data);
export const toggleService = (id) => api.put(`/admin/services/${id}/toggle`).then(r => r.data);
export const uploadServiceImage = (id, file) => {
  const form = new FormData();
  form.append('file', file);
  return api.post(`/admin/services/${id}/upload-image`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }).then(r => r.data);
};

// Promo Slides
export const getPromoSlides = () => api.get('/promos/all').then(r => r.data);
export const createPromoSlide = (data) => api.post('/promos/', data).then(r => r.data);
export const updatePromoSlide = (id, data) => api.put(`/promos/${id}`, data).then(r => r.data);
export const deletePromoSlide = (id) => api.delete(`/promos/${id}`).then(r => r.data);
export const uploadPromoMedia = (id, file) => {
  const form = new FormData();
  form.append('file', file);
  return api.post(`/promos/${id}/upload-media`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }).then(r => r.data);
};

// Announcements
export const getAnnouncements = () => api.get('/announcements/all').then(r => r.data);
export const createAnnouncement = (data) => api.post('/announcements/', data).then(r => r.data);
export const updateAnnouncement = (id, data) => api.put(`/announcements/${id}`, data).then(r => r.data);
export const deleteAnnouncement = (id) => api.delete(`/announcements/${id}`).then(r => r.data);

// Analytics
export const getAnalyticsSummary = (area) => {
  let url = '/admin/analytics/summary';
  if (area) url += `?area=${area}`;
  return api.get(url).then(r => r.data);
};
export const getAnalyticsOrders = (period, area) => {
  let url = `/admin/analytics/orders?period=${period}`;
  if (area) url += `&area=${area}`;
  return api.get(url).then(r => r.data);
};
export const getAnalyticsAreas = () => api.get('/admin/analytics/areas').then(r => r.data);
export const getAnalyticsServices = (area) => {
  let url = '/admin/analytics/services-popular';
  if (area) url += `?area=${area}`;
  return api.get(url).then(r => r.data);
};

// Coupons
export const getCoupons = () => api.get('/coupons/admin/all').then(r => r.data);
export const createCoupon = (data) => api.post('/coupons/admin/create', data).then(r => r.data);
export const updateCoupon = (id, data) => api.put(`/coupons/admin/${id}`, data).then(r => r.data);
export const deleteCoupon = (id) => api.delete(`/coupons/admin/${id}`).then(r => r.data);

export default api;
